import {
  Node as TiptapNode,
  mergeAttributes,
  textblockTypeInputRule,
} from '@tiptap/core';

export type WysiwygHeadingLevel = 1 | 2 | 3 | 4;

interface WysiwygHeadingOptions {
  levels: WysiwygHeadingLevel[];
  HTMLAttributes: Record<string, unknown>;
}

const WYSIWYG_PASSIVE_HEADING_LEVELS: WysiwygHeadingLevel[] = [1, 2, 3, 4];
const WYSIWYG_CREATABLE_HEADING_LEVELS: WysiwygHeadingLevel[] = [2, 3];

function isSupportedHeadingLevel(level: unknown): level is WysiwygHeadingLevel {
  return WYSIWYG_PASSIVE_HEADING_LEVELS.includes(level as WysiwygHeadingLevel);
}

function isCreatableHeadingLevel(level: unknown): level is WysiwygHeadingLevel {
  return WYSIWYG_CREATABLE_HEADING_LEVELS.includes(
    level as WysiwygHeadingLevel
  );
}

const WysiwygHeading = TiptapNode.create<WysiwygHeadingOptions>({
  name: 'heading',

  addOptions() {
    return {
      levels: WYSIWYG_PASSIVE_HEADING_LEVELS,
      HTMLAttributes: {},
    };
  },

  content: 'inline*',
  group: 'block',
  defining: true,

  addAttributes() {
    return {
      level: {
        default: 2,
        rendered: false,
      },
    };
  },

  parseHTML() {
    return this.options.levels.map((level) => ({
      tag: `h${level}`,
      attrs: { level },
    }));
  },

  renderHTML({ node, HTMLAttributes }) {
    const level = isSupportedHeadingLevel(node.attrs['level'])
      ? node.attrs['level']
      : 2;

    return [
      `h${level}`,
      mergeAttributes(this.options.HTMLAttributes, HTMLAttributes),
      0,
    ];
  },

  addCommands() {
    return {
      setHeading:
        (attributes: { level: number }) =>
        ({ commands }) => {
          if (!isCreatableHeadingLevel(attributes.level)) {
            return false;
          }

          return commands.setNode(this.name, attributes);
        },
      toggleHeading:
        (attributes: { level: number }) =>
        ({ commands }) => {
          if (!isCreatableHeadingLevel(attributes.level)) {
            return false;
          }

          return commands.toggleNode(this.name, 'paragraph', attributes);
        },
    };
  },

  addKeyboardShortcuts() {
    return {
      'Mod-Alt-1': () => this.editor.commands.toggleHeading({ level: 2 }),
      'Mod-Alt-2': () => this.editor.commands.toggleHeading({ level: 3 }),
      'Mod-Alt-3': () => true,
      'Mod-Alt-4': () => true,
    };
  },

  addInputRules() {
    return [
      textblockTypeInputRule({
        find: /^(#)\s$/,
        type: this.type,
        getAttributes: { level: 2 },
      }),
      textblockTypeInputRule({
        find: /^(##)\s$/,
        type: this.type,
        getAttributes: { level: 3 },
      }),
    ];
  },
});

export default WysiwygHeading;
